import React, { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Deterministic URL — never changes — so the browser preload in index.html
// matches exactly and the image is served from cache on first paint.
const LOGO_URL = `${BACKEND_URL}/api/notify/assets/wishlink-logo`;

// Module-level state so we only probe the endpoint once per session.
// Values: undefined = unknown (still resolving), true = available, false = 404/error.
let _logoOk;
let _logoOkPromise;

function checkOnce() {
  if (_logoOk !== undefined) return Promise.resolve(_logoOk);
  if (_logoOkPromise) return _logoOkPromise;
  _logoOkPromise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { _logoOk = true; resolve(true); };
    img.onerror = () => { _logoOk = false; resolve(false); };
    img.src = LOGO_URL;
  });
  return _logoOkPromise;
}
// Kick off the probe immediately when this module loads — by the time any
// component mounts and asks, the answer is already known.
if (typeof window !== "undefined") { checkOnce(); }

/**
 * <WishlinkLogo /> — renders the Wishlink workspace logo with zero flash-of-text.
 *
 * - URL is deterministic (matches the preload in index.html)
 * - If the asset 404s (no logo uploaded yet), gracefully falls back to a
 *   text wordmark — but only after we've confirmed the asset is unavailable,
 *   never while the image is still loading.
 *
 * Props:
 *   className — Tailwind classes for the <img> (height, max-width, etc.)
 *   fallbackClassName — Tailwind classes for the text fallback wordmark
 *   alt — image alt text (default "Wishlink")
 */
export default function WishlinkLogo({ className = "", fallbackClassName = "", alt = "Wishlink" }) {
  const [ok, setOk] = useState(_logoOk);
  useEffect(() => {
    if (_logoOk !== undefined) return;
    let alive = true;
    checkOnce().then((v) => { if (alive) setOk(v); });
    return () => { alive = false; };
  }, []);

  // While resolving (only on the very first hit), reserve space but render nothing.
  // This avoids the "wishlink" text flashing before the image arrives.
  if (ok === undefined) {
    return <span aria-hidden="true" className={className} style={{ visibility: "hidden" }} />;
  }
  if (ok === false) {
    return (
      <span className={fallbackClassName || "font-display font-semibold tracking-tight text-[var(--brand)]"}>wishlink</span>
    );
  }
  return <img src={LOGO_URL} alt={alt} className={className} draggable={false} />;
}

// Exposed for places (e.g. the Settings preview tile) that want the raw URL.
export const WISHLINK_LOGO_URL = LOGO_URL;
